/**
 * What a stored table needs before it can be shown read-only: a scroll
 * container, and its header row in a `<thead>`.
 *
 * Scroll container. A table with enough columns is wider than the document
 * column and has to go SOMEWHERE. In the editor it already does: Tiptap's
 * `TableView` builds `div.tableWrapper > table`, and `kiao-prose.css` treats
 * that class as the same horizontal scroll container it gives a blog table.
 * The read-only snapshot is a plain HTML string with no such wrapper, so
 * without this the same table would push the whole PAGE sideways.
 *
 * Header row. The schema serializes every row into `<tbody>`, while
 * `.kiao-prose` hangs its header treatment (mono label row over an ink rule) on
 * `thead th` — the shape the blog's renderer produces. So an all-header first
 * row is promoted here, on the same condition `promoteHeaderRow` in
 * `blog-render.ts` uses. The editor cannot be given a `<thead>` (ProseMirror
 * owns its rows); `kiao-prose.css` draws the same condition there with its
 * `.ProseMirror` header-row rule.
 *
 * Both happen at display time rather than being stored, which keeps one shape
 * in the database: `<div>` and `<thead>` are not in the meeting-note schema, so
 * a stored copy would be rewritten on the next parse anyway (and taken for a
 * document change in the process). Reusing Tiptap's own wrapper class rather
 * than inventing one is the point — a single rule then covers both surfaces.
 *
 * Pure string work, no DOM. The input is never arbitrary HTML: both read-only
 * callers hand over the schema's own output — `normalizeMeetingNotesHtml` for
 * a public share, `renderMeetingNotesHtml` for the template preview — and the
 * schema limits a cell's content to paragraphs, so a table can never contain
 * another table and the non-greedy ranges below are exact. That is the same
 * argument `blog-render.ts` rests its `TABLE_PATTERN` on.
 *
 * The one thing the wrapper does NOT copy from the editor is `tabindex`, and
 * that is deliberate — see {@link wrapTablesInScrollContainer}.
 */

/** A whole table, capturing its attributes and its body. Non-greedy is exact: no nesting. */
const TABLE_PATTERN = /<table((?:\s[^>]*)?)>([\s\S]*?)<\/table>/g

/** A table body's first row, plus whatever rows follow it. */
const TABLE_BODY_PATTERN = /^<tbody>(<tr>[\s\S]*?<\/tr>)([\s\S]*)<\/tbody>$/

/**
 * Moves an all-`<th>` first row of every table into `<thead>`.
 *
 * A first row that mixes `<th>` with `<td>` is a body row with a row header and
 * stays where it is. A header-only table gets no empty `<tbody>`. A table that
 * already has a `<thead>` does not match the body pattern and is left alone.
 */
export function promoteHeaderRows(html: string): string {
  return html.replace(TABLE_PATTERN, (table, attributes: string, body: string) => {
    const match = TABLE_BODY_PATTERN.exec(body)
    if (!match) return table
    const [, firstRow, rest] = match
    if (!firstRow.includes('<th') || firstRow.includes('<td')) return table
    const head = `<thead>${firstRow}</thead>`
    return `<table${attributes}>${rest.length === 0 ? head : `${head}<tbody>${rest}</tbody>`}</table>`
  })
}

/**
 * Wraps every `<table>` in `div.tableWrapper`.
 *
 * Attributes on the table are carried through untouched — the schema emits
 * none today, and re-serializing what it might emit tomorrow is how the two
 * surfaces would drift apart.
 *
 * `tabindex="0"` is on the wrapper because clipping content is only an
 * improvement if the clipped part is still reachable. Before the wrapper
 * existed, a wide table pushed the PAGE sideways — ugly, but the page scrolls
 * with the arrow keys, so a keyboard-only reader could still get to the last
 * column. An `overflow-x` box does not: Chrome gives a scrollable `<div>` no
 * focus of its own, so without this attribute the columns past the fold would
 * be mouse-and-touch only. The editable copy needs nothing equivalent — the
 * caret is already inside it.
 *
 * The cost is one tab stop per table even when the table fits and nothing is
 * hidden. Avoiding that means measuring in the browser, and this surface is a
 * string inserted once with no script attached to it; an unconditional stop is
 * the cheaper of the two mistakes.
 */
export function wrapTablesInScrollContainer(html: string): string {
  return html.replace(
    TABLE_PATTERN,
    (_match, attributes: string, body: string) =>
      `<div class="tableWrapper" tabindex="0"><table${attributes}>${body}</table></div>`
  )
}
